import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useAuth, useHasPermission } from '../hooks/useAuth';

/**
 * Protected route component
 * Redirects to login if not authenticated
 * Checks user permissions for routes
 * Requirements: 1.3
 */

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermission?: string;
  redirectTo?: string;
}

const ProtectedRoute = ({
  children,
  requiredPermission,
  redirectTo = '/login',
}: ProtectedRouteProps) => {
  const { isAuthenticated, currentWorkspace } = useAuth();
  const hasPermission = useHasPermission(requiredPermission || '');
  const location = useLocation();

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // Authenticated but no workspace selected - redirect to workspace selector
  if (!currentWorkspace && location.pathname !== '/workspace-selector') {
    return <Navigate to="/workspace-selector" replace />;
  }

  // Check permission if required
  if (requiredPermission && !hasPermission) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <h2>Access Denied</h2>
          <p>You don't have permission to access this page.</p>
        </Box>
      </Box>
    );
  }

  return <>{children}</>;
};

/**
 * Loading component for route transitions
 */
export const RouteLoader = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <CircularProgress />
    </Box>
  );
};

export default ProtectedRoute;
